import { useContext, useEffect, useMemo } from "react";

import {
  Undefinable,
  convertTimestampToDateNumber,
  PaymentMethod,
  FormType,
  CommandFormEmbedVersion,
  getField,
  getAvailableAddOnCount,
  TicketingFormCategory,
} from "@simplyk/common";
import { useSearchParams } from "next/navigation";
import { useRouter } from "next/router";

// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { useGetSuggestedAmountsAbTest } from "../../canvas/features/FormV2/contexts/helpers/useGetSuggestedAmountsAbTest";
import { FrontendFormContext } from "../contexts/FrontendFormContext";
import { useLocaleContext } from "../contexts/LocaleContext";
import { appendUserProperty } from "../helpers/amplitude";
import { isDonationForm, isTicketing } from "../helpers/form";
import { OCCURRENCE_ID_QUERY_PARAM } from "../helpers/ticketingOccurrence";
import { getSuggestedAmountsTracking } from "../helpers/suggestedAmountsTracking";

import { useMediaQuery } from "./useMediaQuery";
import { useScreenSize } from "./useScreenSize";

import { AmplitudeEvents, FormVisitParams } from "@/common/constants/amplitude";
import { getTrackedQueryParams } from "@/common/hooks/amplitude/helpers/getTrackedQueryParams";
import { useLogVisitPageAmplitudeEvent } from "@/common/hooks/amplitude/useLogVisitPageAmplitudeEvent";
import {
  DonationFormFrontendPageOutput,
  FormOrganizationOutput,
  TicketingFrontendPageOutput,
} from "@/common/types/trpc";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { AmplitudeContext } from "@/components/PaymentProcessor/type";

interface DonationFormAmplitudeContextProps {
  formData: DonationFormFrontendPageOutput | TicketingFrontendPageOutput;
  formAmplitudeContext: AmplitudeContext;
}

export const useLogFormAmplitude = ({ formData, formAmplitudeContext }: DonationFormAmplitudeContextProps) => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const query = getTrackedQueryParams(searchParams);
  const { locale, isoLocale } = useLocaleContext();
  const { displayExpressCheckout, displayAddressQuestion } = useContext(FrontendFormContext);

  const donationFormField =
    "donationFormFields" in formData && formData.donationFormFields
      ? getField(formData.donationFormFields, locale)
      : undefined;
  const suggestedAmountsExperiment =
    "suggestedAmountsExperiment" in formData && formData.suggestedAmountsExperiment
      ? formData.suggestedAmountsExperiment
      : null;

  const { suggestedAmountsExperimentData } = useGetSuggestedAmountsAbTest(
    suggestedAmountsExperiment,
    donationFormField,
    false
  );

  const availableAddOnsCount = isTicketing(formData) ? getAvailableAddOnCount(formData.rates) : 0;

  const params: FormVisitParams = {
    count_custom_questions: formData.questions?.length || 0,
    display_address_question: ((isDonationForm(formData) || isTicketing(formData)) && displayAddressQuestion) || null,
    encourage_monthly_donations: isDonationForm(formData) && formData.encourageMonthlyDonations,
    form_id: formData.id,
    form_type: "formCategory" in formData ? (formData.formCategory ?? FormType.Ticketing) : formData.category,
    has_a_linked_ticketing: isDonationForm(formData) && Boolean(formData.redirectUrl),
    has_additional_donation: isTicketing(formData) && formData.extraDonation,
    has_banner: Boolean(formData.bannerUrl),
    has_banner_video: Boolean(formData.bannerVideoUrl),
    has_check_option: (isDonationForm(formData) || isTicketing(formData)) && formData.chequeMinimumAmount !== null,
    has_in_honour_option: isDonationForm(formData) && Boolean(formData.allowInHonour),
    has_logo: Boolean(formData.logoUrl),
    has_no_suggested_amount: formAmplitudeContext.hasNoSuggestedAmount || null,
    has_preselected_date: isTicketing(formData) && searchParams.has(OCCURRENCE_ID_QUERY_PARAM),
    has_receipt: isDonationForm(formData) && formData.hasReceipt,
    has_receipt_message: formAmplitudeContext.hasReceiptMessage || null,
    has_target: isDonationForm(formData)
      ? Boolean(formData.target)
      : isTicketing(formData)
        ? Boolean(formData.thermometerTarget)
        : false,
    is_embed: query.embed === "true" || formAmplitudeContext.formEmbedVersion !== null,
    is_sweepstakes: isTicketing(formData) && formData.formCategory === TicketingFormCategory.Sweepstakes,
    embed_version:
      formAmplitudeContext.formEmbedVersion ?? (query.embed === "true" ? CommandFormEmbedVersion.EmbedV1 : null),
    is_primary_team: isDonationForm(formData) && formData.isPrimaryTeam,
    is_primary_campaign: isDonationForm(formData) && formData.isPrimaryCampaign,
    iso_locale: isoLocale,
    locale,
    organization_country: formData.organization?.country || null,
    AB_test_cashapp: false,
    fast_checkout_available: displayExpressCheckout,
    suggested_amounts_experiment_data: suggestedAmountsExperimentData?.experimentVariant,
    suggested_amounts_displayed: suggestedAmountsExperimentData?.experimentOutput,
    has_taxes: formHasSalesTaxes(formData),
    form_has_addons: availableAddOnsCount > 0,
    available_addons_count: availableAddOnsCount,
    ...(donationFormField ? getSuggestedAmountsTracking(donationFormField.donationFormAmounts) : {}),
  };

  useEffect(() => {
    if (formData.organization) {
      const { closingDate } = formData.organization;
      if (closingDate) {
        const formattedClosingDate = convertTimestampToDateNumber(closingDate);
        appendUserProperty("donor_organization_closing_date", formattedClosingDate);
      }
    }
  }, [formData.organization]);

  useLogVisitPageAmplitudeEvent({
    event: AmplitudeEvents.DonorFormViewed,
    payload: {
      isForm: true,
      path: router.pathname,
      ...query,
      ...formAmplitudeContext,
      ...params,
    },
  });
};

export const useFormAmplitudeContext = ({
  formData,
  field,
  isEmbed,
  formEmbedVersion,
  organization,
  selectedPaymentMethod,
}: {
  formData: DonationFormFrontendPageOutput | TicketingFrontendPageOutput;
  field?: { donationFormAmounts?: { id: string; amount?: number | null }[] | null };
  isEmbed: boolean;
  formEmbedVersion: CommandFormEmbedVersion | null;
  organization: FormOrganizationOutput | null;
  selectedPaymentMethod: Undefinable<PaymentMethod>;
}) => {
  const { isSmallScreen } = useMediaQuery();
  const { screenSize } = useScreenSize();

  const formAmplitudeContext = useMemo(
    () => ({
      paymentMethod: selectedPaymentMethod,
      isMobile: isSmallScreen,
      isEmbed,
      formEmbedVersion,
      screenSize,
      hasReceiptMessage: isDonationForm(formData) && formData.hasReceipt && Boolean(organization?.canGenerateReceipt),
      hasNoSuggestedAmount: field ? !field.donationFormAmounts?.some((amount) => (amount.amount ?? 0) > 0) : undefined,
      hasTaxes: formHasSalesTaxes(formData),
      form_has_group_membership:
        isTicketing(formData) && (formData.rates ?? []).some((rate) => Boolean(rate.groupMembershipMaxMembers)),
      is_sweepstakes: isTicketing(formData) && formData.formCategory === TicketingFormCategory.Sweepstakes,
      verified_badge_variant: formData.verifiedBadgeVariant ?? null,
    }),
    [
      selectedPaymentMethod,
      isSmallScreen,
      isEmbed,
      formEmbedVersion,
      screenSize,
      formData,
      organization?.canGenerateReceipt,
      field,
    ]
  );

  return { formAmplitudeContext };
};

const formHasSalesTaxes = (formData: DonationFormFrontendPageOutput | TicketingFrontendPageOutput): boolean =>
  isTicketing(formData) && (formData.rates ?? []).some((rate) => (rate.taxes?.length ?? 0) > 0);
